'use client';

/**
 * 114 SOURATES — Coran Complet (mushaf)
 * Index complet des 114 sourates (recherche + filtres Mecquoise/Médinoise)
 * + lecteur mushaf : texte Uthmani local, traduction à la demande (API /api/quran),
 *   audio par verset (CDN islamic.network, numéros globaux), sourate complète,
 *   lecture vocale TTS, navigation précédent/suivant, favoris.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '@/lib/store';
import { DICTS } from '@/lib/i18n';
import { EDITIONS } from '@/lib/quran-data';
import { speakText, stopSpeech } from '@/lib/speech';
import { Search, Play, Pause, Square, Volume2, BookOpen, ArrowLeft, ArrowRight, Loader2, Bookmark, BookmarkCheck, Volume1 } from 'lucide-react';
import { cn } from '@/lib/utils';
import surahsData from '@/data/surahs.json';
import names99 from '@/data/names99.json';
import PageBanner from './PageBanner';

interface SurahMetaRow {
  number: number; name: string; translit: string; meaningEn: string; meaningFr: string;
  ayahs: number; type: 'meccan' | 'medinan';
}
const SURAHS = surahsData as SurahMetaRow[];

// Numéros globaux : offset cumulé des sourates précédentes
const OFFSETS: number[] = (() => {
  const o = [0];
  for (let i = 1; i < 114; i++) o.push(o[i - 1] + SURAHS[i - 1].ayahs);
  return o;
})();
const globalAyah = (surah: number, ayah: number) => OFFSETS[surah - 1] + ayah;

const BISMALA = 'بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ';

export default function SurahsSection() {
  const { uiLang, bookmarks, toggleBookmark, setNowPlaying, markRead, markListened } = useApp();
  const t = DICTS[uiLang];
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'meccan' | 'medinan'>('all');
  const [openSurah, setOpenSurah] = useState<number | null>(null);
  const [ed, setEd] = useState('fr.hamidullah');
  const [translation, setTranslation] = useState<string[]>([]);
  const [loadingEd, setLoadingEd] = useState(false);
  const [playMode, setPlayMode] = useState<'none' | 'ayah' | 'surah'>('none');
  const [currentAyah, setCurrentAyah] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [arabic, setArabic] = useState<string[]>([]);
  const [loadingText, setLoadingText] = useState(false);

  const meta = openSurah ? SURAHS[openSurah - 1] : null;

  // Réinitialisation des états de chargement quand la sourate/l'édition change
  // (ajustement d'état pendant le rendu — conforme aux règles React)
  const [textResetFor, setTextResetFor] = useState<number | null>(null);
  const [edResetFor, setEdResetFor] = useState('');
  const edKey = `${openSurah}:${ed}`;
  if (textResetFor !== openSurah) {
    setTextResetFor(openSurah);
    if (openSurah !== null) { setLoadingText(true); setTranslation([]); }
  }
  if (edResetFor !== edKey) {
    setEdResetFor(edKey);
    if (openSurah !== null) setLoadingEd(true);
  }

  // Chargement du texte (local via /public → fetch léger)
  useEffect(() => {
    if (openSurah === null) return;
    stopSpeech();
    fetch(`/data/quran_full.json`)
      .then((r) => r.json())
      .then((full) => {
        setArabic(full[String(openSurah)] || []);
        setLoadingText(false);
      })
      .catch(() => setLoadingText(false));
    return () => { audioRef.current?.pause(); };
  }, [openSurah]);

  // Traduction à la demande via API route
  useEffect(() => {
    if (openSurah === null) return;
    fetch(`/api/quran?surah=${openSurah}&ed=${ed}`)
      .then((r) => r.json())
      .then((d) => setTranslation(d.translation || []))
      .catch(() => setTranslation([]))
      .finally(() => setLoadingEd(false));
  }, [openSurah, ed]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SURAHS.filter((s) => {
      if (filter !== 'all' && s.type !== filter) return false;
      if (!q) return true;
      return (
        String(s.number) === q ||
        s.translit.toLowerCase().includes(q) ||
        s.meaningFr.toLowerCase().includes(q) ||
        s.meaningEn.toLowerCase().includes(q) ||
        s.name.includes(query.trim())
      );
    });
  }, [query, filter]);

  // Lecture audio du mushaf
  const playAyahAudio = (ayahIdx: number) => {
    if (!audioRef.current) audioRef.current = new Audio();
    const el = audioRef.current;
    el.pause();
    const g = globalAyah(openSurah!, ayahIdx + 1);
    el.src = `https://cdn.islamic.network/quran/audio/128/ar.alafasy/${g}.mp3`;
    setPlayMode('ayah');
    setCurrentAyah(ayahIdx);
    markRead(openSurah!, ayahIdx + 1);
    markListened(openSurah!, ayahIdx + 1);
    setNowPlaying({ surah: openSurah!, ayah: ayahIdx + 1 });
    el.onended = () => {
      if (ayahIdx + 1 < arabic.length) playAyahAudio(ayahIdx + 1);
      else { setPlayMode('none'); setCurrentAyah(null); setNowPlaying(null); }
    };
    el.onerror = () => { setPlayMode('none'); setCurrentAyah(null); setNowPlaying(null); };
    el.play().catch(() => { setPlayMode('none'); setCurrentAyah(null); });
  };

  const playFullSurah = () => {
    if (playMode === 'surah') { audioRef.current?.pause(); setPlayMode('none'); return; }
    if (!audioRef.current) audioRef.current = new Audio();
    const el = audioRef.current;
    el.pause();
    el.src = `https://cdn.islamic.network/quran/audio-surah/128/ar.alafasy/${openSurah}.mp3`;
    setPlayMode('surah');
    setNowPlaying({ surah: openSurah!, ayah: 1 });
    el.onended = () => { setPlayMode('none'); setNowPlaying(null); };
    el.onerror = () => { setPlayMode('none'); setNowPlaying(null); playAyahAudio(0); };
    el.play().catch(() => { setPlayMode('none'); playAyahAudio(0); });
  };

  const stopAudio = () => {
    audioRef.current?.pause();
    setPlayMode('none'); setCurrentAyah(null); setNowPlaying(null);
    stopSpeech();
  };

  const speakSurahTitle = () => {
    if (!meta) return;
    speakText(`سورة ${meta.name.replace(/^سُورَةُ\s*/, '')}`, 'ar');
  };

  const edName = (id: string) => EDITIONS.find((e) => e.id === id)?.name || id;

  /* ============ VUE INDEX : 114 sourates ============ */
  if (openSurah === null) {
    return (
      <>
        <PageBanner
          src="/images/banners/surahs_banner.png"
          eyebrow={uiLang === 'ar' ? 'القرآن الكامل' : uiLang === 'en' ? 'Complete Quran' : 'Coran Complet'}
          title={uiLang === 'ar' ? 'السور الـ١١٤' : uiLang === 'en' ? 'The 114 Surahs' : 'Les 114 Sourates'}
          sub={uiLang === 'ar' ? 'القرآن الكريم كاملاً — ٦٢٣٦ آية بالرسم العثماني مع الترجمات'
            : uiLang === 'en' ? 'The complete Quran — 6,236 verses in Uthmani script with translations'
            : 'Le Coran complet — 6 236 versets en écriture Uthmani avec traductions et audio'}
        />
        <section id="section-sourates" className="relative mx-auto max-w-7xl px-4 py-14 sm:px-6">

        {/* Recherche + filtres */}
        <div className="mx-auto mt-8 flex max-w-3xl flex-col items-center gap-4 sm:flex-row">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={uiLang === 'ar' ? 'ابحث عن سورة…' : uiLang === 'en' ? 'Search a surah…' : 'Rechercher une sourate…'}
              className="w-full rounded-full border border-accent/30 bg-emerald-950/40 py-2.5 pl-10 pr-4 text-foreground placeholder:text-muted-foreground/60 focus:border-accent focus:outline-none"
            />
          </div>
          <div className="flex gap-2">
            {(['all', 'meccan', 'medinan'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={cn(
                  'rounded-full px-4 py-2 text-sm font-medium transition-colors',
                  filter === f ? 'bg-accent text-emerald-950' : 'border border-accent/30 text-foreground hover:bg-accent/10'
                )}
              >
                {f === 'all' ? (uiLang === 'ar' ? 'الكل' : uiLang === 'en' ? 'All' : 'Toutes')
                  : f === 'meccan' ? t.read.meccan : t.read.medinan}
              </button>
            ))}
          </div>
        </div>

        {/* Grille des 114 sourates */}
        <div className="mt-8 grid grid-cols-1 gap-3 min-[520px]:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
          {filtered.map((s) => (
            <button
              key={s.number}
              onClick={() => setOpenSurah(s.number)}
              className="group relative flex items-center gap-3 rounded-xl border border-accent/15 bg-gradient-to-br from-emerald-950/60 to-black/40 p-3.5 text-left transition-all hover:border-accent/60 hover:shadow-[0_0_20px_rgba(212,175,55,0.15)]"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent/15 text-sm font-bold text-accent">
                {s.number}
              </span>
              <span className="min-w-0 flex-1">
                <span dir="ltr" className="block truncate text-left text-sm font-semibold text-foreground rtl:text-right">{s.translit}</span>
                <span dir="ltr" className="block truncate text-left text-xs text-muted-foreground rtl:text-right">
                  {uiLang === 'en' ? s.meaningEn : s.meaningFr} · {s.ayahs} {t.read.verses}
                </span>
              </span>
              <span className={cn('absolute top-2 ltr:right-2 rtl:left-2 h-1.5 w-1.5 rounded-full', s.type === 'meccan' ? 'bg-amber-400' : 'bg-emerald-400')} title={s.type === 'meccan' ? t.read.meccan : t.read.medinan} />
              <span className="font-arabic text-xl text-accent/90 transition-transform group-hover:scale-110">{s.name.replace(/^سُورَةُ\s*/, '')}</span>
            </button>
          ))}
        </div>
        <p className="mt-6 text-center text-xs text-muted-foreground">
          {filtered.length} / 114 {uiLang === 'ar' ? 'سورة' : uiLang === 'en' ? 'surahs' : 'sourates'}
        </p>
        </section>
      </>
    );
  }

  /* ============ VUE LECTEUR MUSHAF ============ */
  const isBookmarked = (i: number) => bookmarks.some((b) => b.surah === openSurah && b.ayah === i + 1);

  return (
    <section id="section-sourates" className="relative mx-auto max-w-5xl px-4 py-16 sm:px-6">
      {/* Navigation */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => { setOpenSurah(null); stopAudio(); }} className="flex items-center gap-2 rounded-full border border-accent/30 px-4 py-2 text-sm text-foreground hover:bg-accent/10">
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
          {uiLang === 'ar' ? 'كل السور' : uiLang === 'en' ? 'All surahs' : 'Toutes les sourates'}
        </button>
        <div className="flex items-center gap-2">
          <button
            disabled={openSurah! <= 1}
            onClick={() => setOpenSurah(Math.max(1, openSurah! - 1))}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-accent/30 text-foreground disabled:opacity-30 hover:bg-accent/10"
          >
            <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
          </button>
          <button
            disabled={openSurah! >= 114}
            onClick={() => setOpenSurah(Math.min(114, openSurah! + 1))}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-accent/30 text-foreground disabled:opacity-30 hover:bg-accent/10"
          >
            <ArrowRight className="h-4 w-4 rtl:rotate-180" />
          </button>
        </div>
      </div>

      {/* En-tête sourate */}
      <div className="mt-8 rounded-2xl border border-accent/25 bg-gradient-to-b from-emerald-950/70 to-black/40 p-8 text-center">
        <div className="flex items-center justify-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-accent text-lg font-bold text-accent">{meta!.number}</span>
          <div className="text-left rtl:text-right">
            <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">{meta!.translit}</h2>
            <p className="text-sm text-muted-foreground">{uiLang === 'en' ? meta!.meaningEn : meta!.meaningFr} · {meta!.ayahs} {t.read.verses} · {meta!.type === 'meccan' ? t.read.meccan : t.read.medinan}</p>
          </div>
        </div>
        <p className="mt-4 font-arabic text-3xl text-accent">{meta!.name.replace(/^سُورَةُ\s*/, 'سُورَةُ ')}</p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          {/* Boutons OR — exigence utilisateur : boutons dorés sous le nom de la sourate */}
          <button
            onClick={playFullSurah}
            className={cn(
              'flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-all',
              playMode === 'surah'
                ? 'bg-gradient-to-b from-[#f5e29a] to-[#d4af37] text-[#032019] ring-2 ring-[#f0d98c]/90 shadow-[0_0_26px_rgba(212,175,55,0.65)]'
                : 'bg-gradient-to-b from-[#e8c86a] to-[#b8933a] text-[#032019] shadow-[0_4px_18px_rgba(212,175,55,0.35)] hover:from-[#f2da96] hover:to-[#c9a13f]'
            )}
          >
            {playMode === 'surah' ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            {uiLang === 'ar' ? 'السورة كاملة' : uiLang === 'en' ? 'Full surah' : 'Sourate complète'}
          </button>
          <button
            onClick={playMode === 'none' ? () => playAyahAudio(0) : stopAudio}
            className={cn(
              'flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-all',
              playMode === 'ayah'
                ? 'bg-gradient-to-b from-[#f5e29a] to-[#d4af37] text-[#032019] ring-2 ring-[#f0d98c]/90 shadow-[0_0_26px_rgba(212,175,55,0.65)]'
                : 'bg-gradient-to-b from-[#e8c86a] to-[#b8933a] text-[#032019] shadow-[0_4px_18px_rgba(212,175,55,0.35)] hover:from-[#f2da96] hover:to-[#c9a13f]'
            )}
          >
            {playMode === 'ayah' ? <Square className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            {uiLang === 'ar' ? 'آية بآية' : uiLang === 'en' ? 'Verse by verse' : 'Verset par verset'}
          </button>
          <button
            onClick={speakSurahTitle}
            className="flex items-center gap-2 rounded-full bg-gradient-to-b from-[#e8c86a] to-[#b8933a] px-4 py-2 text-sm font-bold text-[#032019] shadow-[0_4px_18px_rgba(212,175,55,0.35)] transition-all hover:from-[#f2da96] hover:to-[#c9a13f]"
          >
            <Volume1 className="h-4 w-4" /> {uiLang === 'ar' ? 'اسم السورة' : uiLang === 'en' ? 'Speak name' : 'Dire le nom'}
          </button>
        </div>
      </div>

      {/* Sélecteur de traduction */}
      <div className="mt-6 flex flex-wrap items-center gap-3 rounded-xl border border-accent/15 bg-emerald-950/30 px-4 py-3">
        <BookOpen className="h-4 w-4 text-accent" />
        <select
          value={ed}
          onChange={(e) => setEd(e.target.value)}
          className="flex-1 min-w-[220px] rounded-lg border border-accent/30 bg-emerald-950 px-3 py-2 text-sm text-foreground focus:outline-none"
        >
          {EDITIONS.map((e) => (
            <option key={e.id} value={e.id}>{e.flag} {e.name}</option>
          ))}
        </select>
        {loadingEd && <Loader2 className="h-4 w-4 animate-spin text-accent" />}
        <span className="text-xs text-muted-foreground">{edName(ed)}</span>
      </div>

      {/* Basmala */}
      {openSurah !== 1 && openSurah !== 9 && (
        <p dir="rtl" className="mt-8 text-center font-arabic text-2xl text-accent/90">{BISMALA}</p>
      )}

      {/* Versets */}
      {loadingText ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>
      ) : (
        <div className="mt-6 space-y-5">
          {arabic.map((text, i) => (
            <article
              key={i}
              className={cn(
                'group relative rounded-xl border p-5 transition-colors',
                currentAyah === i ? 'border-accent bg-accent/10' : 'border-accent/15 bg-emerald-950/25 hover:border-accent/40'
              )}
            >
              <div className="flex items-start gap-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent/15 text-xs font-bold text-accent">
                  {openSurah}:{i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p dir="rtl" lang="ar" className="font-arabic text-2xl leading-[2.2] text-foreground">{text}</p>
                  {translation[i] !== undefined && (
                    <p className={cn('mt-3 border-l-2 border-accent/40 pl-3 text-sm italic text-muted-foreground', uiLang === 'ar' && 'border-l-0 border-r-2 pr-3 pl-0 text-right not-italic')}>
                      {translation[i]}
                    </p>
                  )}
                  {translation[i] === undefined && !loadingEd && (
                    <p className="mt-3 text-xs text-muted-foreground/60">—</p>
                  )}
                </div>
                <div className="flex shrink-0 flex-col gap-2">
                  <button
                    onClick={() => (playMode === 'ayah' && currentAyah === i ? stopAudio() : playAyahAudio(i))}
                    title={t.read.play_verse}
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-accent/10 text-accent hover:bg-accent/25"
                  >
                    {playMode === 'ayah' && currentAyah === i ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                  </button>
                  <button
                    onClick={() => speakText(text.replace(/[\u06DE\u06E9]/g, ''), 'ar', 0.6)}
                    title="TTS"
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-accent/10 text-accent hover:bg-accent/25"
                  >
                    <Volume1 className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => toggleBookmark(openSurah!, i + 1)}
                    className={cn('flex h-8 w-8 items-center justify-center rounded-full', isBookmarked(i) ? 'bg-accent text-emerald-950' : 'bg-accent/10 text-accent hover:bg-accent/25')}
                  >
                    {isBookmarked(i) ? <BookmarkCheck className="h-3.5 w-3.5" /> : <Bookmark className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Navigation bas */}
      <div className="mt-10 flex items-center justify-between">
        <button
          disabled={openSurah! <= 1}
          onClick={() => { setOpenSurah(Math.max(1, openSurah! - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          className="flex items-center gap-2 rounded-full border border-accent/30 px-5 py-2.5 text-sm text-foreground disabled:opacity-30 hover:bg-accent/10"
        >
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
          {openSurah! > 1 ? SURAHS[openSurah! - 2].translit : ''}
        </button>
        <button
          disabled={openSurah! >= 114}
          onClick={() => { setOpenSurah(Math.min(114, openSurah! + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          className="flex items-center gap-2 rounded-full border border-accent/30 px-5 py-2.5 text-sm text-foreground disabled:opacity-30 hover:bg-accent/10"
        >
          {openSurah! < 114 ? SURAHS[openSurah!].translit : ''}
          <ArrowRight className="h-4 w-4 rtl:rotate-180" />
        </button>
      </div>
    </section>
  );
}
